import React, { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  MessageSquare,
  Clock,
  Send,
  Trash2,
  Check,
  ExternalLink,
  ChevronRight,
  CheckCircle2,
  Filter,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";

// ── Types ─────────────────────────────────────────────────

interface VideoReviewComment {
  id: string;
  taskId: string;
  userId: string;
  authorName: string;
  text: string;
  timestampSeconds: string | null;
  timestampEnd: string | null;
  status: "open" | "resolved";
  parentId: string | null;
  createdAt: string;
}

interface VideoReviewTabProps {
  taskId: string;
  videoUrl?: string;
  currentUserName?: string;
}

type VideoSource = "google-drive" | "youtube" | "html5" | "none";
type CommentMode = "none" | "single" | "range";
type CommentFilter = "all" | "open" | "resolved";

type YouTubePlayerState = -1 | 0 | 1 | 2 | 3 | 5;

interface YouTubePlayer {
  destroy: () => void;
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getIframe: () => HTMLIFrameElement;
}

interface YouTubePlayerEvent {
  target: YouTubePlayer;
  data: YouTubePlayerState;
}

interface YouTubeNamespace {
  Player: new (
    element: string | HTMLElement,
    config: {
      videoId: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: YouTubePlayerEvent) => void;
        onStateChange?: (event: YouTubePlayerEvent) => void;
        onError?: (event: { data: number }) => void;
      };
    }
  ) => YouTubePlayer;
}

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiPromise: Promise<YouTubeNamespace> | null = null;

// ── Helpers ───────────────────────────────────────────────

function detectSource(url: string): VideoSource {
  if (!url) return "none";
  if (url.includes("drive.google.com")) return "google-drive";
  if (getYouTubeVideoId(url)) return "youtube";
  return "html5";
}

function getYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return id && id.length === 11 ? id : null;
    }

    if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
      const watchId = parsed.searchParams.get("v");
      if (watchId && watchId.length === 11) return watchId;

      const parts = parsed.pathname.split("/").filter(Boolean);
      const candidate = parts[1];
      if (
        (parts[0] === "embed" || parts[0] === "shorts" || parts[0] === "live") &&
        candidate?.length === 11
      ) {
        return candidate;
      }
    }
  } catch {
    const match = url.match(
      /(?:v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/
    );
    if (match) return match[1];
  }

  return null;
}

function toGDriveEmbed(url: string): string {
  const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch) return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/file/d/${openMatch[1]}/preview`;
  return url;
}

function loadYouTubeIframeApi(): Promise<YouTubeNamespace> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YOUTUBE_API_SERVER_SIDE"));
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeApiPromise) {
    return youtubeApiPromise;
  }

  youtubeApiPromise = new Promise<YouTubeNamespace>((resolve, reject) => {
    const existingScript = document.getElementById(
      "youtube-iframe-api"
    ) as HTMLScriptElement | null;
    const previousReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      if (window.YT?.Player) {
        resolve(window.YT);
        return;
      }
      reject(new Error("YOUTUBE_API_LOADED_INCORRECTLY"));
    };

    if (existingScript) {
      existingScript.addEventListener("error", () => reject(new Error("YOUTUBE_API_LOAD_FAILED")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = "youtube-iframe-api";
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => reject(new Error("YOUTUBE_API_LOAD_FAILED"));
    document.head.appendChild(script);
  });

  return youtubeApiPromise;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseTimestamp(str: string): number | null {
  const m = str.trim().match(/^(\d+):(\d{1,2})$/);
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

function timeAgo(iso: string, t: TFunction): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("time.now");
  if (mins < 60) return t("time.minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("time.hoursAgo", { count: hours });
  return t("time.daysAgo", { count: Math.floor(hours / 24) });
}

function getTs(c: VideoReviewComment): number | null {
  return c.timestampSeconds !== null ? parseFloat(c.timestampSeconds) : null;
}

function getTsEnd(c: VideoReviewComment): number | null {
  return c.timestampEnd !== null ? parseFloat(c.timestampEnd) : null;
}

function normalizeTime(value: number): number {
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function getYouTubeErrorMessage(code: number, t: TFunction): string {
  const errorMap: Record<number, string> = {
    2: t("errors.ytInvalidUrl"),
    5: t("errors.ytEmbedNotSupported"),
    100: t("errors.ytVideoUnavailable"),
    101: t("errors.ytEmbedBlocked"),
    150: t("errors.ytEmbedBlocked"),
    153: t("errors.ytConfigRejected"),
  };
  return errorMap[code] ?? t("errors.ytPlaybackError");
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-red-500/30 text-red-300 rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Component ─────────────────────────────────────────────

export function VideoReviewTab({ taskId, videoUrl, currentUserName = "Ty" }: VideoReviewTabProps) {
  const { t } = useTranslation("videoReviewTab");
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const youtubeContainerRef = useRef<HTMLDivElement>(null);
  const youtubePlayerRef = useRef<YouTubePlayer | null>(null);
  const youtubeSyncIntervalRef = useRef<number | null>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);

  const [commentMode, setCommentMode] = useState<CommentMode>("none");
  const [commentText, setCommentText] = useState("");
  const [rangeIn, setRangeIn] = useState<number | null>(null);
  const [rangeOut, setRangeOut] = useState<number | null>(null);
  const [manualIn, setManualIn] = useState("");
  const [manualOut, setManualOut] = useState("");

  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [filter, setFilter] = useState<CommentFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const source = videoUrl ? detectSource(videoUrl) : "none";
  const supportsLiveTimestamps = source === "html5" || source === "youtube";
  const youtubeVideoId = videoUrl ? getYouTubeVideoId(videoUrl) : null;

  // ── YouTube player lifecycle ───────────────────────────

  const stopYouTubeSync = useCallback(() => {
    if (youtubeSyncIntervalRef.current !== null) {
      window.clearInterval(youtubeSyncIntervalRef.current);
      youtubeSyncIntervalRef.current = null;
    }
  }, []);

  const syncYouTubeState = useCallback((player?: YouTubePlayer | null) => {
    const activePlayer = player ?? youtubePlayerRef.current;
    if (!activePlayer) return;
    setCurrentTime(normalizeTime(activePlayer.getCurrentTime()));
    setDuration(normalizeTime(activePlayer.getDuration()));
  }, []);

  const startYouTubeSync = useCallback(() => {
    if (youtubeSyncIntervalRef.current !== null) return;
    youtubeSyncIntervalRef.current = window.setInterval(() => syncYouTubeState(), 250);
  }, [syncYouTubeState]);

  const destroyYouTubePlayer = useCallback(() => {
    stopYouTubeSync();
    youtubePlayerRef.current?.destroy();
    youtubePlayerRef.current = null;
    if (youtubeContainerRef.current) youtubeContainerRef.current.innerHTML = "";
  }, [stopYouTubeSync]);

  // ── Data ───────────────────────────────────────────────

  const { data } = useQuery<{ comments: VideoReviewComment[] }>({
    queryKey: [`/api/pipeline/review/${taskId}`],
    enabled: !!taskId,
  });

  const allComments = data?.comments ?? [];

  const topLevel = allComments
    .filter((c) => c.parentId === null)
    .sort((a, b) => {
      const ta = getTs(a);
      const tb = getTs(b);
      if (ta !== null && tb !== null) return ta - tb;
      if (ta !== null) return -1;
      if (tb !== null) return 1;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  const q = searchQuery.trim().toLowerCase();
  const filteredComments = topLevel.filter((c) => {
    if (filter === "open" && c.status !== "open") return false;
    if (filter === "resolved" && c.status !== "resolved") return false;
    if (q && !c.text.toLowerCase().includes(q) && !c.authorName.toLowerCase().includes(q))
      return false;
    return true;
  });

  const openCount = topLevel.filter((c) => c.status === "open").length;
  const resolvedCount = topLevel.filter((c) => c.status === "resolved").length;

  const repliesOf = (parentId: string) =>
    allComments
      .filter((c) => c.parentId === parentId)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const markerComments = topLevel.filter((c) => getTs(c) !== null);

  // ── Mutations ──────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: (payload: {
      text: string;
      timestampSeconds: number | null;
      timestampEnd: number | null;
      parentId: string | null;
    }) => apiRequest("POST", `/api/pipeline/review/${taskId}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/pipeline/review/${taskId}`] });
      setCommentText("");
      setRangeIn(null);
      setRangeOut(null);
      setManualIn("");
      setManualOut("");
      setReplyingTo(null);
      setReplyText("");
      setCommentMode("none");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/pipeline/review/comment/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [`/api/pipeline/review/${taskId}`] }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/pipeline/review/comment/${id}/status`, { status }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [`/api/pipeline/review/${taskId}`] }),
  });

  // ── Player handlers ────────────────────────────────────

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const seekTo = useCallback(
    (seconds: number) => {
      if (source === "youtube") {
        const player = youtubePlayerRef.current;
        if (!player) return;
        player.seekTo(seconds, true);
        player.playVideo();
        setCurrentTime(seconds);
        return;
      }
      if (videoRef.current) {
        videoRef.current.currentTime = seconds;
        videoRef.current.play().catch(() => {});
      }
    },
    [source]
  );

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration || !timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const nextTime = ((e.clientX - rect.left) / rect.width) * duration;
    seekTo(nextTime);
    setCurrentTime(nextTime);
  };

  const handleComposeFocus = useCallback(() => {
    if (source === "html5" && videoRef.current && !videoRef.current.paused)
      videoRef.current.pause();
    if (source === "youtube") youtubePlayerRef.current?.pauseVideo();
  }, [source]);

  // Spacja play/pause dla HTML5
  useEffect(() => {
    if (source !== "html5") return;
    const handler = (e: KeyboardEvent) => {
      if (
        e.code !== "Space" ||
        (e.target as HTMLElement).tagName === "TEXTAREA" ||
        (e.target as HTMLElement).tagName === "INPUT"
      )
        return;
      e.preventDefault();
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [source]);

  // Reset on source change
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setYoutubeError(null);
    if (source !== "youtube") destroyYouTubePlayer();
    return () => {
      if (source !== "youtube") stopYouTubeSync();
    };
  }, [source, videoUrl, destroyYouTubePlayer, stopYouTubeSync]);

  // YouTube player wiring
  useEffect(() => {
    if (source !== "youtube" || !youtubeVideoId || !youtubeContainerRef.current) return;

    let cancelled = false;
    destroyYouTubePlayer();

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled || !youtubeContainerRef.current) return;

        const player = new YT.Player(youtubeContainerRef.current, {
          videoId: youtubeVideoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            enablejsapi: 1,
            fs: 1,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              youtubePlayerRef.current = event.target;
              const iframe = event.target.getIframe();
              iframe.setAttribute(
                "allow",
                "autoplay; fullscreen; encrypted-media; picture-in-picture"
              );
              syncYouTubeState(event.target);
            },
            onStateChange: (event) => {
              if (cancelled) return;
              syncYouTubeState(event.target);
              if (event.data === 1) {
                startYouTubeSync();
                return;
              }
              if (event.data === 0 || event.data === 2 || event.data === 5) stopYouTubeSync();
            },
            onError: (event) => {
              if (cancelled) {
                stopYouTubeSync();
                setYoutubeError(getYouTubeErrorMessage(event.data, t));
              }
            },
          },
        });
        youtubePlayerRef.current = player;
      })
      .catch((error) => {
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : "";
          const ytApiErrorMap: Record<string, string> = {
            YOUTUBE_API_SERVER_SIDE: t("errors.ytNotServer"),
            YOUTUBE_API_LOADED_INCORRECTLY: t("errors.ytLoadedIncorrectly"),
            YOUTUBE_API_LOAD_FAILED: t("errors.ytLoadFailed"),
          };
          setYoutubeError(ytApiErrorMap[msg] ?? t("errors.ytStartFailed"));
        }
      });

    return () => {
      cancelled = true;
      destroyYouTubePlayer();
    };
  }, [
    source,
    youtubeVideoId,
    destroyYouTubePlayer,
    startYouTubeSync,
    stopYouTubeSync,
    syncYouTubeState,
  ]);

  // Active comment sync
  useEffect(() => {
    const markers = allComments.filter((c) => c.parentId === null && c.timestampSeconds !== null);
    if (!markers.length) {
      setActiveCommentId(null);
      return;
    }

    let active: string | null = null;
    for (const c of markers) {
      const ts = parseFloat(c.timestampSeconds!);
      const tsEnd = c.timestampEnd !== null ? parseFloat(c.timestampEnd) : null;
      if (tsEnd !== null) {
        if (currentTime >= ts && currentTime <= tsEnd) {
          active = c.id;
          break;
        }
      } else {
        if (ts <= currentTime) active = c.id;
      }
    }
    setActiveCommentId(active);
  }, [currentTime, allComments]);

  // ── Submit handlers ────────────────────────────────────

  const handleSubmit = () => {
    const text = commentText.trim();
    if (!text) return;
    let tsStart: number | null = null;
    let tsEnd: number | null = null;

    if (commentMode === "single") {
      tsStart = supportsLiveTimestamps ? currentTime : parseTimestamp(manualIn);
    } else if (commentMode === "range") {
      if (supportsLiveTimestamps) {
        tsStart = rangeIn;
        tsEnd = rangeOut;
      } else {
        tsStart = parseTimestamp(manualIn);
        tsEnd = parseTimestamp(manualOut);
      }
    }
    addMutation.mutate({ text, timestampSeconds: tsStart, timestampEnd: tsEnd, parentId: null });
  };

  const handleReplySubmit = (parentId: string) => {
    const text = replyText.trim();
    if (!text) return;
    addMutation.mutate({ text, timestampSeconds: null, timestampEnd: null, parentId });
  };

  const handleToggleMode = (mode: CommentMode) => {
    setCommentMode((prev) => (prev === mode ? "none" : mode));
    setRangeIn(null);
    setRangeOut(null);
    setManualIn("");
    setManualOut("");
  };

  const handleCancelCompose = () => {
    setCommentText("");
    setCommentMode("none");
    setRangeIn(null);
    setRangeOut(null);
    setManualIn("");
    setManualOut("");
  };

  // ── Render ─────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Video player ── */}
      <div className="shrink-0">
        <div
          className="relative bg-black rounded-xl overflow-hidden"
          style={{ aspectRatio: "16/9" }}
        >
          {!videoUrl ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 gap-2">
              <MessageSquare size={32} />
              <p className="text-xs">{t("noVideo")}</p>
            </div>
          ) : source === "google-drive" ? (
            <iframe
              src={toGDriveEmbed(videoUrl)}
              className="w-full h-full"
              allow="autoplay"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : source === "youtube" ? (
            <>
              <div ref={youtubeContainerRef} className="h-full w-full" />
              {youtubeError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 px-6 text-center">
                  <p className="text-sm font-medium text-white">{t("youtubeEmbedFailed")}</p>
                  <p className="max-w-md text-xs text-white/60">{youtubeError}</p>
                </div>
              )}
            </>
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full"
              controls
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
            />
          )}
        </div>

        {/* External helpers */}
        {source === "youtube" && videoUrl && (
          <div className="flex justify-between items-center pt-1.5">
            <p className="text-[11px] text-white/20">
              {youtubeError ? t("youtubeEmbedHint") : t("youtubeTimestampHint")}
            </p>
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[11px] text-white/25 hover:text-white/60 transition-colors"
            >
              <ExternalLink size={10} />
              {t("youtubeOpenExternal")}
            </a>
          </div>
        )}
        {source === "google-drive" && videoUrl && (
          <p className="pt-1.5 text-[11px] text-white/20">{t("gdriveHint")}</p>
        )}
        {source === "html5" && <p className="pt-1 text-[10px] text-white/15">{t("html5Hint")}</p>}

        {/* Timeline */}
        {supportsLiveTimestamps && duration > 0 && (
          <div className="pt-2 pb-1">
            <div
              ref={timelineRef}
              className="relative h-2.5 bg-white/[0.06] rounded-full cursor-pointer"
              onClick={handleTimelineClick}
            >
              <div
                className="absolute left-0 top-0 h-full bg-red-600/60 rounded-full pointer-events-none"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              {markerComments.map((c) => {
                const ts = getTs(c)!;
                const tsEnd = getTsEnd(c);
                const isActive = activeCommentId === c.id;
                const isResolved = c.status === "resolved";

                if (tsEnd !== null) {
                  return (
                    <button
                      key={c.id}
                      title={`${formatTime(ts)} → ${formatTime(tsEnd)} — ${c.text}`}
                      style={{
                        left: `${(ts / duration) * 100}%`,
                        width: `${Math.max(((tsEnd - ts) / duration) * 100, 1)}%`,
                      }}
                      className={cn(
                        "absolute top-0 h-full rounded-full z-10 transition-opacity",
                        isResolved
                          ? "bg-emerald-400/40 hover:bg-emerald-400/60"
                          : isActive
                            ? "bg-red-400/80"
                            : "bg-red-400/40 hover:bg-red-400/70"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        seekTo(ts);
                      }}
                    />
                  );
                }

                return (
                  <button
                    key={c.id}
                    title={`${formatTime(ts)} — ${c.text}`}
                    style={{ left: `${(ts / duration) * 100}%` }}
                    className={cn(
                      "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-[#111] z-20 transition-transform hover:scale-125",
                      isResolved
                        ? "bg-emerald-400"
                        : isActive
                          ? "bg-red-400 scale-125"
                          : "bg-red-400/70"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      seekTo(ts);
                    }}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-white/20 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Comments panel ── */}
      <div className="flex flex-col flex-1 min-h-0 mt-3 border border-white/[0.08] rounded-xl overflow-hidden">
        {/* Panel header */}
        <div className="shrink-0 border-b border-white/[0.08] bg-white/[0.02]">
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <MessageSquare size={13} className="text-red-500" />
              <span className="text-sm font-semibold text-white">{t("commentsHeader")}</span>
              {openCount > 0 && (
                <span className="bg-red-600/25 text-red-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {openCount}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Search toggle */}
              <button
                onClick={() => {
                  setSearchOpen((v) => {
                    if (v) setSearchQuery("");
                    else setTimeout(() => searchInputRef.current?.focus(), 50);
                    return !v;
                  });
                }}
                className={cn(
                  "w-7 h-7 flex items-center justify-center rounded-md transition-colors",
                  searchOpen
                    ? "bg-red-600/20 text-red-400"
                    : "text-white/30 hover:text-white/60 hover:bg-white/[0.05]"
                )}
              >
                <Search size={13} />
              </button>

              {/* Filter tabs */}
              <div className="flex items-center gap-0.5 ml-1">
                <Filter size={10} className="text-white/20 mr-0.5" />
                {(["all", "open", "resolved"] as CommentFilter[]).map((f) => {
                  const labels: Record<CommentFilter, string> = {
                    all: t("filters.all"),
                    open: t("filters.open"),
                    resolved: t("filters.resolved"),
                  };
                  const counts: Record<CommentFilter, number> = {
                    all: topLevel.length,
                    open: openCount,
                    resolved: resolvedCount,
                  };
                  return (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        "px-2 py-1 rounded-md text-[10px] font-medium transition-colors",
                        filter === f
                          ? "bg-red-600/20 text-red-400"
                          : "text-white/25 hover:text-white/50"
                      )}
                    >
                      {labels[f]}
                      {counts[f] > 0 ? ` ${counts[f]}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="px-4 pb-2.5">
              <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-1.5 focus-within:border-red-500/40 transition-colors">
                <Search size={12} className="text-white/30 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setSearchOpen(false);
                      setSearchQuery("");
                    }
                  }}
                  placeholder={t("searchPlaceholder")}
                  className="flex-1 bg-transparent text-xs text-white placeholder:text-white/25 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-white/30 hover:text-white/60 shrink-0"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Comment list */}
        <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2 min-h-0">
          {filteredComments.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-white/20 gap-2">
              <MessageSquare size={22} />
              <p className="text-xs">
                {filter === "all"
                  ? t("empty.all")
                  : filter === "open"
                    ? t("empty.open")
                    : t("empty.resolved")}
              </p>
            </div>
          )}

          {filteredComments.map((comment, idx) => {
            const commentNumber = topLevel.indexOf(comment) + 1;
            const ts = getTs(comment);
            const tsEnd = getTsEnd(comment);
            const isActive = activeCommentId === comment.id;
            const isResolved = comment.status === "resolved";
            const replies = repliesOf(comment.id);

            return (
              <div key={comment.id} className="flex flex-col gap-1">
                <div
                  className={cn(
                    "group flex gap-2.5 p-3 rounded-xl border transition-all",
                    isResolved
                      ? "border-emerald-500/15 bg-emerald-500/[0.03] opacity-60"
                      : isActive
                        ? "border-red-500/30 bg-red-500/[0.04]"
                        : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.10]"
                  )}
                >
                  <div
                    className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5",
                      isResolved
                        ? "bg-emerald-600/20 text-emerald-400"
                        : "bg-red-600/25 text-red-400"
                    )}
                  >
                    {initials(comment.authorName)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span
                        className={cn(
                          "text-xs font-medium",
                          isResolved ? "text-white/40" : "text-white/75"
                        )}
                      >
                        {comment.authorName}
                      </span>
                      <span className="text-[10px] text-white/20 font-mono">#{commentNumber}</span>
                      {ts !== null && (
                        <button
                          onClick={() => seekTo(ts)}
                          className="flex items-center gap-1 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-400 hover:bg-red-500/25 transition-colors"
                        >
                          <Clock size={9} />
                          {tsEnd !== null
                            ? `${formatTime(ts)} → ${formatTime(tsEnd)}`
                            : formatTime(ts)}
                        </button>
                      )}
                      {isResolved && (
                        <span className="flex items-center gap-0.5 text-[10px] text-emerald-400/70">
                          <CheckCircle2 size={10} /> {t("resolvedBadge")}
                        </span>
                      )}
                      <span className="text-[10px] text-white/20 ml-auto">
                        {timeAgo(comment.createdAt, t)}
                      </span>
                    </div>

                    <p
                      className={cn(
                        "text-xs leading-relaxed",
                        isResolved ? "text-white/25 line-through" : "text-white/70"
                      )}
                    >
                      {highlight(comment.text, q)}
                    </p>

                    <div className="flex items-center gap-3 mt-1.5">
                      <button
                        onClick={() =>
                          statusMutation.mutate({
                            id: comment.id,
                            status: isResolved ? "open" : "resolved",
                          })
                        }
                        disabled={statusMutation.isPending}
                        className={cn(
                          "text-[10px] transition-colors",
                          isResolved
                            ? "text-emerald-400/50 hover:text-white/40"
                            : "text-white/20 hover:text-emerald-400"
                        )}
                      >
                        {isResolved ? t("status.undo") : t("status.resolve")}
                      </button>
                      <button
                        onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                        className="text-[10px] text-white/20 hover:text-white/50 transition-colors"
                      >
                        ↩ {t("replyButton")} {replies.length > 0 && `(${replies.length})`}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => deleteMutation.mutate(comment.id)}
                    className="opacity-0 group-hover:opacity-100 text-white/20 hover:text-red-400 transition-all shrink-0 self-start mt-0.5"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>

                {/* Replies */}
                {replies.length > 0 && (
                  <div className="ml-9 flex flex-col gap-1">
                    {replies.map((reply) => (
                      <div
                        key={reply.id}
                        className="group flex gap-2 p-2.5 rounded-lg border border-white/[0.04] bg-white/[0.01]"
                      >
                        <div className="w-5 h-5 rounded-full bg-red-600/20 flex items-center justify-center text-[9px] font-bold text-red-400 shrink-0 mt-0.5">
                          {initials(reply.authorName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[11px] font-medium text-white/55">
                              {reply.authorName}
                            </span>
                            <span className="text-[10px] text-white/20 ml-auto">
                              {timeAgo(reply.createdAt, t)}
                            </span>
                          </div>
                          <p className="text-[11px] text-white/55 leading-relaxed">{reply.text}</p>
                        </div>
                        <button
                          onClick={() => deleteMutation.mutate(reply.id)}
                          className="opacity-0 group-hover:opacity-100 text-white/20 hover:text-red-400 transition-all shrink-0 self-start"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply compose */}
                {replyingTo === comment.id && (
                  <div className="ml-9 flex gap-2">
                    <div className="flex-1 bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 flex items-center gap-2 focus-within:border-red-500/30 transition-colors">
                      <input
                        type="text"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleReplySubmit(comment.id);
                          if (e.key === "Escape") {
                            setReplyingTo(null);
                            setReplyText("");
                          }
                        }}
                        placeholder={t("replyPlaceholder")}
                        autoFocus
                        className="flex-1 bg-transparent text-xs text-white placeholder:text-white/20 focus:outline-none"
                      />
                      <button
                        onClick={() => handleReplySubmit(comment.id)}
                        disabled={!replyText.trim() || addMutation.isPending}
                        className="text-white/30 hover:text-red-400 disabled:opacity-30 transition-colors shrink-0"
                      >
                        <Send size={11} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Compose area (pinned bottom of panel) ── */}
        <div className="shrink-0 border-t border-white/[0.08] bg-white/[0.01] p-3">
          <div className="flex gap-2.5">
            {/* Avatar */}
            <div className="w-7 h-7 rounded-full bg-red-600/25 flex items-center justify-center text-[10px] font-bold text-red-400 shrink-0 mt-0.5">
              {initials(currentUserName)}
            </div>

            {/* Input area */}
            <div className="flex-1 min-w-0">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onFocus={handleComposeFocus}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
                }}
                placeholder={t("composePlaceholder")}
                rows={commentText ? 3 : 1}
                className="w-full bg-transparent text-sm text-white placeholder:text-white/25 resize-none focus:outline-none leading-relaxed"
              />

              {/* Timestamp options */}
              {source !== "none" && (
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <ModeCheckbox
                    label="Timestamp"
                    checked={commentMode === "single"}
                    onChange={() => handleToggleMode("single")}
                  />
                  <ModeCheckbox
                    label={t("modeRange")}
                    checked={commentMode === "range"}
                    onChange={() => handleToggleMode("range")}
                  />

                  {commentMode === "single" &&
                    (supportsLiveTimestamps ? (
                      <span className="flex items-center gap-1 text-[11px] text-red-400 font-mono">
                        <Clock size={10} />
                        {formatTime(currentTime)}
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={manualIn}
                        onChange={(e) => setManualIn(e.target.value)}
                        placeholder="0:00"
                        maxLength={7}
                        className="w-14 bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5 text-xs text-red-400 placeholder:text-red-500/40 focus:outline-none text-center"
                      />
                    ))}

                  {commentMode === "range" &&
                    (supportsLiveTimestamps ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setRangeIn(currentTime)}
                          className={cn(
                            "rounded px-2 py-0.5 text-[11px] border transition-colors",
                            rangeIn !== null
                              ? "bg-red-500/20 border-red-500/40 text-red-400"
                              : "border-white/10 text-white/35 hover:border-white/25"
                          )}
                        >
                          {t("rangeFrom")}: {rangeIn !== null ? formatTime(rangeIn) : "—"}
                        </button>
                        <ChevronRight size={11} className="text-white/20 shrink-0" />
                        <button
                          onClick={() => setRangeOut(currentTime)}
                          className={cn(
                            "rounded px-2 py-0.5 text-[11px] border transition-colors",
                            rangeOut !== null
                              ? "bg-red-500/20 border-red-500/40 text-red-400"
                              : "border-white/10 text-white/35 hover:border-white/25"
                          )}
                        >
                          {t("rangeTo")}: {rangeOut !== null ? formatTime(rangeOut) : "—"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={manualIn}
                          onChange={(e) => setManualIn(e.target.value)}
                          placeholder="0:00"
                          maxLength={7}
                          className="w-14 bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5 text-xs text-red-400 placeholder:text-red-500/40 focus:outline-none text-center"
                        />
                        <ChevronRight size={11} className="text-white/20" />
                        <input
                          type="text"
                          value={manualOut}
                          onChange={(e) => setManualOut(e.target.value)}
                          placeholder="0:00"
                          maxLength={7}
                          className="w-14 bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5 text-xs text-red-400 placeholder:text-red-500/40 focus:outline-none text-center"
                        />
                      </div>
                    ))}
                </div>
              )}

              {/* Actions */}
              {(commentText.trim() || commentMode !== "none") && (
                <div className="flex items-center justify-end gap-2 mt-2.5">
                  <button
                    onClick={handleCancelCompose}
                    className="text-xs text-white/35 hover:text-white/60 px-2 py-1 transition-colors"
                  >
                    {t("cancelButton")}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!commentText.trim() || addMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold transition-colors"
                  >
                    {addMutation.isPending ? t("submitting") : t("submitButton")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────

function ModeCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button onClick={onChange} className="flex items-center gap-1.5 group select-none">
      <div
        className={cn(
          "w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-all shrink-0",
          checked ? "bg-red-600 border-red-600" : "border-white/25 group-hover:border-white/50"
        )}
      >
        {checked && <Check size={8} className="text-white" strokeWidth={3} />}
      </div>
      <span
        className={cn(
          "text-xs transition-colors",
          checked ? "text-red-400 font-medium" : "text-white/35 group-hover:text-white/60"
        )}
      >
        {label}
      </span>
    </button>
  );
}
